/**
 * 画面上部の通知（トースト）。表示はNoticeViewportがお知らせと同じ並びにまとめて受け持つ。
 *
 * 役割と出す条件
 * - success: 利用者の操作が終わり、その結果がその場の画面で見えないとき（ダイアログが閉じた、
 *   別の画面へ移った、コピーした、送信した、バックグラウンドの処理を始めた）。スイッチや選択、
 *   その場での編集のように結果が見える操作と、ブラウザーが知らせるダウンロードでは出さない。
 * - error: 利用者の操作が失敗し、その失敗が画面のほかの場所に出ないとき。入力の誤りは入力欄に、
 *   データの読み込みの失敗はその領域のエラー表示に出し、トーストにしない。
 * - info: 利用者の操作の結果ではないが、気づかないと困ること。
 *
 * 文言
 * - success: 「{対象}を{動作}しました」。1文で、句点を付けない。件数があるときは
 *   「{n}件の{対象}を{動作}しました」。
 * - error: 「{対象}を{動作}できませんでした。」に、原因か次にできることを1文続け、どちらも句点で
 *   終える。次にできることは「時間をおいて再試行してください。」のように書き、言い方をそろえる
 *   （getApiErrorMessageがこの形に整える）。
 * - info: 成功と同じく1文なら句点を付けない。
 *
 * 表示時間
 * - successとinfoは4秒で消える。ポインターを重ねている間とフォーカスがある間は止める。
 * - errorは、失敗の理由と回復の手段を読む前に見失わないよう、閉じるまで残す。
 * - 同じ文言が出ている間は重ねず、表示時間をやり直す。同時に出すのは3件までで、古いものから消す。
 */

export type ToastTone = "success" | "error" | "info";

export type ToastItem = {
  id: number;
  tone: ToastTone;
  message: string;
  /** 出した時刻。同じ文言をもう一度出したときに更新し、表示時間をやり直す。 */
  shownAt: number;
};

export const TOAST_DURATION_MS = 4000;
const TOAST_LIMIT = 3;

let toasts: readonly ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function publish(next: readonly ToastItem[]) {
  toasts = next;
  for (const listener of listeners) listener();
}

function show(tone: ToastTone, message: string) {
  const shownAt = Date.now();
  const existing = toasts.find(
    (item) => item.tone === tone && item.message === message,
  );
  if (existing) {
    publish(
      toasts.map((item) => (item === existing ? { ...item, shownAt } : item)),
    );
    return;
  }
  publish(
    [...toasts, { id: nextId++, tone, message, shownAt }].slice(-TOAST_LIMIT),
  );
}

export const toast = {
  success: (message: string) => show("success", message),
  error: (message: string) => show("error", message),
  info: (message: string) => show("info", message),
};

/** 閉じるアニメーションが終わった通知を一覧から外す。 */
export function removeToast(id: number) {
  publish(toasts.filter((item) => item.id !== id));
}

export function subscribeToasts(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getToasts() {
  return toasts;
}

const NO_TOASTS: readonly ToastItem[] = [];

/** サーバーで描くときは通知を持たない。 */
export function getServerToasts() {
  return NO_TOASTS;
}
